import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { queryFor, useApiConfig } from "@visioni/a3s-client-api/app";
import {
  getFleetClientEndpoint,
  listFleetClientsEndpoint,
  type ClientDetail,
  type ClientSummary,
} from "../../client-messages/clients";
import { useCursorPager } from "./cursor-pager";
import type { UseActivationsResult } from "./hooks";

const listFleetClientsQuery = queryFor(listFleetClientsEndpoint);
const getFleetClientQuery = queryFor(getFleetClientEndpoint);

/** Rows per page of the client list. The endpoint caps at 100. */
export const CLIENTS_PAGE_SIZE = 25;

export interface FleetClientFilters {
  /** The start of a client number. Absent or blank lists every client. */
  readonly clientNo?: string;
}

/** A page of clients, held and paged exactly as a page of activations is. */
export type UseClientsResult = UseActivationsResult<ClientSummary>;

/**
 * One page of the fleet's Patriot clients in client-number order, narrowed to
 * numbers starting with `clientNo`. Changing it returns to page one.
 */
export function useFleetClients(filters: FleetClientFilters): UseClientsResult {
  const apiConfig = useApiConfig();
  const clientNo = filters.clientNo?.trim() ?? "";
  const pager = useCursorPager(clientNo);

  const query = useQuery({
    ...listFleetClientsQuery(
      apiConfig,
      {},
      {
        limit: CLIENTS_PAGE_SIZE,
        ...(clientNo !== "" && { clientNo }),
        ...(pager.cursor !== undefined && { cursor: pager.cursor }),
      },
    ),
    placeholderData: keepPreviousData,
  });

  return {
    items: query.data?.items,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: () => void query.refetch(),
    pager,
    // As in `useTenantActivations`: a placeholder's cursor belongs to the page being left.
    nextCursor: query.isPlaceholderData ? null : (query.data?.cursor ?? null),
  };
}

export interface UseClientResult {
  readonly client: ClientDetail | undefined;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

/** One client by client number, with its groupings resolved. */
export function useFleetClient(clientNo: string): UseClientResult {
  const apiConfig = useApiConfig();

  const query = useQuery({
    ...getFleetClientQuery(apiConfig, { clientNo }),
    enabled: clientNo !== "",
  });

  return {
    client: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}
